import { useState } from 'react';
import { REPLY_CLASS_META, entryRosterCampaign, type EventRecord } from '@lib/care-model';
import { OP_CLASSES } from '@lib/reply-groups';
import { newClientRequestId } from '@lib/care-api';
import {
  EXPLAINER_VIDEOS, KIT_KINDS, MAX_REASON, MAX_WORD_REF, MEMBER_LANGUAGES, MIN_WORD_REF, MORE_VIDEOS_COMING, QUICK_KEY_ROWS, isReplyOp,
  UNSTOP_CARE_LINE_WORDING, languageLabel, pressSentenceOf, quickKey, quickKeyDef, reasonRequired, type DoorOp, type QuickKeyId, type ReplyOp, type UnstopBasis,
} from '@lib/quick-keys';
import { STAGED_WORDING, isStagedAnswer, replyPressHelp, stagedKeySentence } from '@lib/staged-presses';

/**
 * SLICE C: HIS THREE ROWS OF QUICK KEYS in the review drawer (his OneNote page "Instructions: Care",
 * 24 Sep 2026 1:24 PM). Status: Opt In, Opt-Out, Stop, Unstop, Un-opt-out. Action: Resend Kit, Resend
 * Results, Change Address (a window for the new address, written to the master). Explain: Send Explainer
 * Video (the list of videos; more are coming). ROW 640 adds Change Language to the Action row: a window
 * showing the language on file and the plan's languages, whose press records the correction in the master
 * and, when the file disagrees, the issue the client report carries.
 *
 * A key opens its own small panel (the fields it needs and one line of what it records) and the press is
 * made from there, never on the first click. Opt In, Opt-Out and Stop are the reply presses Care already
 * has (the drawer hands them in, so their rules and wording stay one); the other six go to
 * POST /api/care/quick-key, one batch at the master's door with her name. NOTHING here sends anything to a
 * member: a resend or a video is a REQUEST that waits for William's word.
 *
 * The press's id is minted when the panel opens and KEPT when the master did not answer, so pressing again
 * sends the same press and never a second one.
 *
 * WAVE 3 ITEM 4 (William, 2 Oct 2026: "all items go to approval then take effect"): with the deployment
 * staging presses, a key's answer says `staged: true`: the press was QUEUED on the case and takes effect
 * when the case is approved. The result line then reads "Queued for approval ..." in a plain tone, never
 * a done word and never the done tone, and the drawer is told (`onStaged`) so it shows the press in its
 * Waiting for approval block. `pressBlock` is the drawer's guard: while dispositions are ticked but not
 * saved, no key can be pressed, and the reason is said on every key and on the open panel's button.
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */
export interface QuickKeysProps {
  ev: EventRecord;
  /** The drawer's hard lock (another operator's claim, cleared, archived). */
  locked: boolean;
  /** MEMBERCARE_REPLY_BUTTONS: the reply presses exist on this deployment. */
  replyButtons: boolean;
  /** A reply press in flight (the drawer's own busy key). */
  replyBusy: boolean;
  /** The drawer's own reply press (the same path as the reply panel's buttons). */
  onReplyPress: (op: ReplyOp) => void;
  /**
   * Only these keys (William's console review, 2 Oct 2026: the panel left the case; the explainer
   * lives on under Kits in the Action / disposition list, his map 2.3.2 Help: Explainer).
   */
  only?: readonly QuickKeyId[];
  /**
   * Wave 3 item 4: why NO key may be pressed right now ('' or absent = nothing blocks). The drawer
   * passes its unsaved-ticks guard, so a queued press never strands ticked dispositions.
   */
  pressBlock?: string;
  /** Wave 3 item 4: a key's press was queued for approval; the drawer reloads the case to show it. */
  onStaged?: () => void;
  /** BB1 (22 Oct 2026): the Unstop is Care's own line (MEMBERCARE_UNSTOP_CARE_LINE, link off): the staff basis is offered. */
  unstopCareLine?: boolean;
}

/** `queued` (wave 3 item 4) is the plain pill: a press that waits for approval is neither done nor a fault. */
type Tone = 'ok' | 'warn' | 'danger' | 'queued';
const TONE_CLASS: Record<Tone, string> = { ok: 'pill pill-success', warn: 'pill pill-warning', danger: 'pill pill-danger', queued: 'pill' };

/** Why a key cannot be pressed on this entry, or '' when it can. Pure (exported for its test). */
export function quickKeyBlock(id: QuickKeyId, ev: EventRecord, locked: boolean, replyButtons: boolean): string {
  if (locked) return 'This entry is locked (claimed by someone else, cleared or archived).';
  if (!ev.memberId) return 'Match the entry to a member first: every key names the member.';
  if (isReplyOp(id)) {
    if (!replyButtons) return 'The reply presses are off on this deployment (MEMBERCARE_REPLY_BUTTONS).';
    const cls = ev.smsReply?.classification;
    if (!cls) return 'A text reply\'s press. For a call or an email, use Mark as Do Not Contact or the checklist below.';
    if (!OP_CLASSES[id].includes(cls)) {
      return `This reply reads as ${REPLY_CLASS_META[cls]?.label ?? cls}; ${quickKeyDef(id).label} takes ${OP_CLASSES[id].map((c) => REPLY_CLASS_META[c]?.label ?? c).join(', ')}.`;
    }
  }
  return '';
}

export function QuickKeys({ ev, locked, replyButtons, replyBusy, onReplyPress, only, pressBlock = '', onStaged, unstopCareLine = false }: QuickKeysProps) {
  const [open, setOpen] = useState<QuickKeyId | null>(null);
  const [crid, setCrid] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: Tone; text: string } | null>(null);
  const [reason, setReason] = useState('');
  const [basis, setBasis] = useState<UnstopBasis | ''>('');
  const [wordRef, setWordRef] = useState('');
  const [campaignIn, setCampaignIn] = useState('');
  const [kitKind, setKitKind] = useState('');
  const [video, setVideo] = useState('');
  const [addr, setAddr] = useState({ line1: '', line2: '', city: '', state: '', zip: '', zip4: '' });
  const [memberLang, setMemberLang] = useState('');

  const campaign = entryRosterCampaign(ev);
  const snap = ev.memberSnapshot;
  const onFile = snap?.address ? `${snap.address}, ${snap.city}, ${snap.state} ${snap.zip}` : '';
  // The language on file as the case page shows it (the server reads it by the same rule): null when the
  // entry carries no member snapshot, so the screen never guesses what the file says.
  const langOnFile: string | null = snap ? String(snap.language ?? '').trim().toUpperCase() : null;

  const openKey = (id: QuickKeyId) => {
    setOpen(open === id ? null : id);
    setCrid(newClientRequestId());
    setResult(null);
    setReason(''); setBasis(''); setWordRef(''); setCampaignIn(''); setKitKind(''); setVideo(''); setMemberLang('');
    setAddr({ line1: '', line2: '', city: '', state: '', zip: '', zip4: '' });
  };
  const close = () => setOpen(null);

  /** What is still missing for the open door press, or ''. */
  const missing = (op: DoorOp): string => {
    if (reasonRequired(op) && !reason.trim()) return 'Give the reason: it is the record of why.';
    if (reason.length > MAX_REASON) return `The reason is at most ${MAX_REASON} characters.`;
    if (op === 'unstop') {
      if (!basis) return unstopCareLine ? 'Say what lifts the STOP: the member texted START, William\'s word, or Care staff with a reason.' : 'Say what lifts the STOP: the member texted START, or William\'s word.';
      if (basis === 'his_word' && (wordRef.trim().length < MIN_WORD_REF || wordRef.trim().length > MAX_WORD_REF)) return 'Cite his word: where and when he said it.';
    }
    if ((op === 'un-opt-out' || op === 'resend-kit') && !campaign && !/^[a-z0-9][a-z0-9_-]{0,62}$/.test(campaignIn.trim())) {
      return 'The entry names no campaign: type the campaign it is for.';
    }
    if (op === 'change-address') {
      if (!addr.line1.trim() || !addr.city.trim()) return 'The street and the city are required.';
      if (!/^[A-Za-z]{2}$/.test(addr.state.trim())) return 'The state is two letters.';
      if (!/^\d{5}$/.test(addr.zip.trim())) return 'The zip is five digits.';
      if (addr.zip4.trim() && !/^\d{4}$/.test(addr.zip4.trim())) return 'The zip+4 is four digits.';
    }
    if (op === 'explainer-video' && !video) return 'Pick the video.';
    if (op === 'change-language' && !memberLang) return 'Pick the language the member asked for.';
    return '';
  };

  const pressDoor = async (op: DoorOp) => {
    // The guard is checked again at the press itself: a panel opened before the ticks were made.
    if (pressBlock) {
      setResult({ tone: 'warn', text: pressBlock });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await quickKey({
        eventId: ev.eventId, op, clientRequestId: crid,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        ...(op === 'unstop' && basis ? { basis } : {}),
        ...(op === 'unstop' && basis === 'his_word' ? { wordRef: wordRef.trim() } : {}),
        ...(!campaign && campaignIn.trim() ? { campaignId: campaignIn.trim() } : {}),
        ...(op === 'resend-kit' && kitKind ? { kitKind } : {}),
        ...(op === 'change-address' ? { address: { ...addr, state: addr.state.trim().toUpperCase() } } : {}),
        ...(op === 'explainer-video' ? { test: video, language: 'en' } : {}),
        ...(op === 'change-language' ? { memberLanguage: memberLang } : {}),
      });
      // Wave 3 item 4: read `staged` BEFORE anything else of the answer. A queued press is never
      // printed in the answer's done words, whatever else the answer carries.
      if (isStagedAnswer(r)) {
        setResult({ tone: 'queued', text: stagedKeySentence(r, quickKeyDef(op).label) });
        setOpen(null);
        onStaged?.();
        return;
      }
      // Queueing a key writes the case, so it can lose a race with another save of it (409 stale_etag;
      // a press that goes to the master never answers this). Nothing was queued: said in words, and the
      // id is kept, so pressing again is the same press.
      if (r.status === 409 && (r.data as { code?: unknown }).code === 'stale_etag') {
        setResult({ tone: 'warn', text: STAGED_WORDING.keyConflict });
        return;
      }
      const s = pressSentenceOf(r);
      setResult(s);
      if (s.tone === 'ok') {
        setOpen(null);
      } else if (s.tone === 'danger') {
        setCrid(newClientRequestId()); // a refusal is final for that id; a new press gets a new one
      }
    } catch (err) {
      setResult({ tone: 'warn', text: `The master did not answer (${(err as Error).message}). Press again: the same press is sent, never twice.` });
    } finally {
      setBusy(false);
    }
  };

  const def = open ? quickKeyDef(open) : null;
  const doorOp = open && !isReplyOp(open) ? (open as DoorOp) : null;
  // The drawer's guard first (wave 3 item 4), then what the press itself still needs.
  const need = doorOp ? (pressBlock || missing(doorOp)) : '';

  const panel = (() => {
    if (!open || !def) return null;
    if (isReplyOp(open)) {
      return (
        <div className="note" data-testid="qk-panel">
          <div><strong>{def.label}</strong> <span className="muted">{def.help}</span></div>
          {/* I1 a (William, 17 Oct 2026): an Opt Out or Stop key says the block goes on now and the rest waits. */}
          <div className="muted" style={{ marginTop: 4 }}>{replyPressHelp(open)}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button type="button" className="btn btn-sm btn-primary" disabled={replyBusy || !!pressBlock} title={pressBlock || undefined} onClick={() => { onReplyPress(open); close(); }}>
              {`Press ${def.label}`}
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={close}>Cancel</button>
          </div>
        </div>
      );
    }
    const op = open as DoorOp;
    const body = (
      <>
        <div><strong>{def.label}</strong> <span className="muted">{def.help}</span></div>
        {op === 'unstop' && (
          <fieldset style={{ border: 0, padding: 0, margin: '8px 0 0' }}>
            <legend className="micro muted">What lifts the STOP (the phone company keeps its block until the member texts START)</legend>
            <label style={{ display: 'block' }}>
              <input type="radio" name="qk-basis" checked={basis === 'member_start'} onChange={() => setBasis('member_start')} /> The member texted START (Care checks its record)
            </label>
            <label style={{ display: 'block' }}>
              <input type="radio" name="qk-basis" checked={basis === 'his_word'} onChange={() => setBasis('his_word')} /> William&apos;s word
            </label>
            {unstopCareLine && (
              <label style={{ display: 'block' }}>
                <input type="radio" name="qk-basis" checked={basis === 'staff'} onChange={() => setBasis('staff')} /> {UNSTOP_CARE_LINE_WORDING.staff}
              </label>
            )}
            {unstopCareLine && <div className="micro muted" data-testid="qk-unstop-care-line">{UNSTOP_CARE_LINE_WORDING.waits}</div>}
            {basis === 'his_word' && (
              <input className="text-input" aria-label="Where he said it" placeholder="Where and when he said it, e.g. R. 155 row 639" value={wordRef}
                maxLength={MAX_WORD_REF} onChange={(e) => setWordRef(e.target.value)} style={{ marginTop: 4, width: '100%' }} />
            )}
          </fieldset>
        )}
        {(op === 'un-opt-out' || op === 'resend-kit' || op === 'resend-results') && (
          <div className="micro" style={{ marginTop: 6 }}>
            Campaign: {campaign ? <strong>{campaign}</strong> : (
              <input className="text-input" aria-label="Campaign" placeholder="the campaign slug, e.g. 26a1ckedgen" value={campaignIn}
                onChange={(e) => setCampaignIn(e.target.value)} style={{ width: 220 }} />
            )}
          </div>
        )}
        {op === 'resend-kit' && (
          <label className="micro" style={{ display: 'block', marginTop: 6 }}>
            Kit (optional){' '}
            <select aria-label="Kit" value={kitKind} onChange={(e) => setKitKind(e.target.value)}>
              <option value="">as the campaign sends it</option>
              {KIT_KINDS.map((k) => <option key={k} value={k}>{k.toUpperCase()}</option>)}
            </select>
          </label>
        )}
        {op === 'explainer-video' && (
          <fieldset style={{ border: 0, padding: 0, margin: '8px 0 0' }}>
            <legend className="micro muted">The videos</legend>
            {EXPLAINER_VIDEOS.map((v) => (
              <label key={v.test} style={{ display: 'block' }}>
                <input type="radio" name="qk-video" checked={video === v.test} onChange={() => setVideo(v.test)} /> {v.label}
              </label>
            ))}
            <div className="micro muted">{MORE_VIDEOS_COMING}</div>
          </fieldset>
        )}
        {op === 'change-address' && (
          <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
            <div className="micro muted">On file: {onFile || 'none on the roster'}</div>
            <input className="text-input" aria-label="Street" placeholder="Street" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} />
            <input className="text-input" aria-label="Apartment or unit" placeholder="Apartment, unit (optional)" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} />
            <div style={{ display: 'flex', gap: 4 }}>
              <input className="text-input" aria-label="City" placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} style={{ flex: 2 }} />
              <input className="text-input" aria-label="State" placeholder="ST" maxLength={2} value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} style={{ width: 48 }} />
              <input className="text-input" aria-label="Zip" placeholder="Zip" maxLength={5} value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} style={{ width: 72 }} />
              <input className="text-input" aria-label="Zip+4" placeholder="+4" maxLength={4} value={addr.zip4} onChange={(e) => setAddr({ ...addr, zip4: e.target.value })} style={{ width: 52 }} />
            </div>
          </div>
        )}
        {op === 'change-language' && (
          <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
            <div className="micro muted" data-testid="qk-language-on-file">
              On file: {langOnFile === null ? 'not read on this entry (no client issue will be recorded)' : `${languageLabel(langOnFile)} (the plan's file)`}
            </div>
            <label className="micro">
              The member asked for{' '}
              <select aria-label="Member's language" value={memberLang} onChange={(e) => setMemberLang(e.target.value)}>
                <option value="">pick the language</option>
                {MEMBER_LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </label>
            {memberLang && langOnFile !== null && langOnFile !== memberLang && (
              <div className="micro muted" data-testid="qk-language-issue">
                The client report will say: Plan file lists {languageLabel(langOnFile)}; the member asked for {languageLabel(memberLang)}.
              </div>
            )}
            {memberLang && langOnFile === memberLang && (
              <div className="micro muted" data-testid="qk-language-same">The plan&apos;s file already lists {languageLabel(memberLang)}: no client issue will be recorded.</div>
            )}
            <div className="micro muted">Nothing is sent to the member from this press: a text in the new language goes only on William&apos;s word.</div>
          </div>
        )}
        <input className="text-input" aria-label="Reason" value={reason} maxLength={MAX_REASON} onChange={(e) => setReason(e.target.value)}
          placeholder={reasonRequired(op) ? 'Reason (required): why' : 'Reason (optional)'} style={{ marginTop: 8, width: '100%' }} />
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-sm btn-primary" disabled={busy || !!need} title={need || undefined} onClick={() => void pressDoor(op)}>
            {busy ? 'Pressing...' : `Press ${def.label}`}
          </button>
          <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={close}>Cancel</button>
          {need && <span className="micro muted" data-testid="qk-need">{need}</span>}
        </div>
      </>
    );
    // His "Change Address (which opens a window ...)": the address press is a window over the drawer, and the
    // language press (ROW 640) takes the same shape.
    if (op === 'change-address' || op === 'change-language') {
      return (
        <div role="dialog" aria-modal="true" aria-label={def.label} data-testid={op === 'change-address' ? 'qk-address-window' : 'qk-language-window'}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'grid', placeItems: 'center', zIndex: 60 }}>
          <div className="note" style={{ background: 'var(--paper, #fff)', maxWidth: 480, width: '92%', padding: 16 }}>{body}</div>
        </div>
      );
    }
    return <div className="note" data-testid="qk-panel">{body}</div>;
  })();

  const rows = only
    ? QUICK_KEY_ROWS.map((r) => ({ ...r, keys: r.keys.filter((k) => only.includes(k.id)) })).filter((r) => r.keys.length > 0)
    : QUICK_KEY_ROWS;
  return (
    <div style={only ? { padding: '2px 0' } : { padding: '10px 18px', borderBottom: '1px solid var(--ink-900)' }} data-testid={only ? 'quick-keys-only' : 'quick-keys'}>
      {rows.map((r) => (
        <div key={r.row} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }} data-testid={`qk-row-${r.row}`}>
          {!only && <span className="micro muted" style={{ width: 56 }}>{r.row}</span>}
          {r.keys.map((k) => {
            // The key's own reason first (locked, unmatched, not its reply class), then the drawer's guard.
            const block = quickKeyBlock(k.id, ev, locked, replyButtons) || pressBlock;
            return (
              <button key={k.id} type="button" className={`btn btn-sm ${open === k.id ? 'btn-primary' : 'btn-ghost'}`}
                disabled={!!block} title={block || k.help} aria-pressed={open === k.id} onClick={() => openKey(k.id)}>
                {k.label}
              </button>
            );
          })}
        </div>
      ))}
      {!only && <div className="micro muted">Every key is recorded in the master with your name. Nothing is sent to the member from here.</div>}
      {panel}
      {result && <div className={TONE_CLASS[result.tone]} style={{ display: 'block', marginTop: 6, whiteSpace: 'normal' }} data-testid="qk-result">{result.text}</div>}
    </div>
  );
}
